import type { AudioSourceData, MediaSourceData } from '@lumen5/luminary';

import type { TranscriptWord, VideoApprovalStatusEnum } from 'js/src/client';
import { DashboardFlowEnum, ErrorEnum, NarrativeThreadEnum } from 'js/src/client';
import type { ImmutableMap } from 'js/src/libs/consts';

export enum APP_ACTIONS {
    SWITCH_EDITOR_TAB = 'SWITCH_EDITOR_TAB',
    SET_EDITOR_TABS_VISIBILITY = 'SET_EDITOR_TABS_VISIBILITY',
    SET_SELECTED_TEMPLATE_ID = 'SET_SELECTED_TEMPLATE_ID',
    SET_SELECTED_ASPECT_RATIO = 'SET_SELECTED_ASPECT_RATIO',
    SET_SELECTED_PALETTE_INDEX = 'SET_SELECTED_PALETTE_INDEX',
    SET_STAGE = 'SET_STAGE',
    SET_VIDEO_ID = 'SET_VIDEO_ID',
    SET_TOOLBOX_CREATING_VIDEO = 'SET_TOOLBOX_CREATING_VIDEO',
    SET_SCRIPT_COMPOSER_PLACEHOLDER_VIDEO_ID = 'SET_SCRIPT_COMPOSER_PLACEHOLDER_VIDEO_ID',
    SET_SCENE_REVIEWING = 'SET_SCENE_REVIEWING',
    SET_SCENE_HANDOFF_ACTIVE = 'SET_SCENE_HANDOFF_ACTIVE',
    SET_VIDEO_CREATION_SOURCE = 'SET_VIDEO_CREATION_SOURCE',
    SET_IS_ANIMATION_VIDEO = 'SET_IS_ANIMATION_VIDEO',
    SET_PARENT_VIDEO_LANGUAGE = 'SET_PARENT_VIDEO_LANGUAGE',
    SET_VIDEO_NAME = 'SET_VIDEO_NAME',
    SET_VIDEO_LANGUAGE = 'SET_VIDEO_LANGUAGE',
    SET_VIDEO_PROJECT = 'SET_VIDEO_PROJECT',
    SET_VIDEO_APPROVAL_STATUS = 'SET_VIDEO_APPROVAL_STATUS',
    MARK_SCENE_GROUP_AS_LOADING = 'MARK_SCENE_GROUP_AS_LOADING',
    MARK_SCENE_GROUP_AS_FINISHED_LOADING = 'MARK_SCENE_GROUP_AS_FINISHED_LOADING',
    MARK_SCENE_GROUP_AS_SKIPPED = 'MARK_SCENE_GROUP_AS_SKIPPED',
    MARK_SCENE_AS_LOADING = 'MARK_SCENE_AS_LOADING',
    MARK_SCENE_AS_FINISHED_LOADING = 'MARK_SCENE_AS_FINISHED_LOADING',
    SET_IS_STORYBOARD_GENERATING = 'SET_IS_STORYBOARD_GENERATING',
    SET_VIDEO_DATA_HASH = 'SET_VIDEO_DATA_HASH',
    SET_SHARE_PAGE_URL = 'SET_SHARE_PAGE_URL',
    SET_VIDEO_VISIBILITY = 'SET_VIDEO_VISIBILITY',
    SET_VIDEO_OWNER_ID = 'SET_VIDEO_OWNER_ID',
    SET_VIDEO_ASSIGNEE = 'SET_VIDEO_ASSIGNEE',
    SET_VIDEO_FEEDBACK_FORM_STATE = 'SET_VIDEO_FEEDBACK_FORM_STATE',
    SET_RECOMMENDATIONS_COMPLETED = 'SET_RECOMMENDATIONS_COMPLETED',
    SET_WINDOW_WIDTH = 'SET_WINDOW_WIDTH',
    SET_VIDEO_INIT_TYPE = 'SET_VIDEO_INIT_TYPE',
    SET_ADVANCED_MEDIA_CONTROLS_STATE = 'SET_ADVANCED_MEDIA_CONTROLS_STATE',
    SET_BLUEPRINT_ID = 'SET_BLUEPRINT_ID',
    SET_ASYNC_GENERATION_TASK_ID = 'SET_ASYNC_GENERATION_TASK_ID',
    SET_ASYNC_GENERATION_DATA = 'SET_ASYNC_GENERATION_DATA',
    SET_IS_CUSTOMER_BLUEPRINT = 'SET_IS_CUSTOMER_BLUEPRINT',
    SET_RECOMMENDED_BLUEPRINT = 'SET_RECOMMENDED_BLUEPRINT',
    SET_MULTI_VIDEOS = 'SET_MULTI_VIDEOS',
    SET_INITIAL_BLUEPRINT_ID = 'SET_INITIAL_BLUEPRINT_ID',
    SET_MEDIA_OPTIMIZER_STATE = 'SET_MEDIA_OPTIMIZER_STATE',
    SET_DASHBOARD_FLOW = 'SET_DASHBOARD_FLOW',
    SET_TEXT_ON_MEDIA_GENERATING = 'SET_TEXT_ON_MEDIA_GENERATING',
    SET_SKIP_MEDIA_OPTIMIZATION = 'SET_SKIP_MEDIA_OPTIMIZATION',
    SET_VIDEO_CREATED_THIS_SESSION = 'SET_VIDEO_CREATED_THIS_SESSION',
    SET_CREATE_VIDEO_FLOW_ANALYTICS_METHOD = 'SET_CREATE_VIDEO_FLOW_ANALYTICS_METHOD',
    SET_CREATE_VIDEO_FLOW_ANALYTICS_THEME_ID = 'SET_CREATE_VIDEO_FLOW_ANALYTICS_THEME_ID',
    SET_LOADING_CREATOR_ACTIVITY_ID = 'SET_LOADING_CREATOR_ACTIVITY_ID',
    SET_IS_EDITING_DRAFT = 'SET_IS_EDITING_DRAFT',
    SET_PROJECT_ID = 'SET_PROJECT_ID',
    SET_AUTO_CREATE_SCENE_GROUP_FROM_UPLOADED_ASSET_UUID = 'SET_AUTO_CREATE_SCENE_GROUP_FROM_UPLOADED_ASSET_UUID',
    SET_SELECTED_BRAND_KIT_ID = 'SET_SELECTED_BRAND_KIT_ID',
    SET_PENDING_CHART = 'SET_PENDING_CHART',
    SET_CHART_SETTINGS_STATE = 'SET_CHART_SETTINGS_STATE',
    SET_TRANSITION_SETTINGS_STATE = 'SET_TRANSITION_SETTINGS_STATE',
}

export type PendingChart = {
    sceneId: string;
    assetId: string;
    chartType: string;
};

/* OTHER TYPES */
export enum EDITOR_TAB_OPTIONS {
    MEDIA = 'media',
    MUSIC = 'music',
    STYLE = 'style',
    FORMAT = 'format',
    ELEMENTS = 'elements',
    VOICEOVER = 'voiceover',
    ORIGINAL_CONTENT = 'original-conent',
    STAFF_CONTROLS = 'staff-controls',
    DAM = 'dam',
    GENERATIVE_MEDIA = 'generative-media',
    CHAT = 'chat',
    SMART_ACTIONS = 'smart-actions',
    NONE = 'none',
}

export enum STAGES {
    INIT = 'init',
    ONBOARDING = 'welcome',
    LOADING_DOC_TO_SCRIPT = 'loading-doc-to-script',
    LOADING_EDIT_VIDEO = 'loading-edit-video',
    CREATOR = 'creator',
    NEW_CREATE_VIDEO_FLOW_PAGE = 'new-create-video-flow-page',
}

export function isDashboardVideoFlow(value: unknown): value is DashboardFlowEnum {
    return (
        typeof value === 'string' &&
        Object.values(DashboardFlowEnum).includes(value as DashboardFlowEnum)
    );
}

export enum SCENE_GROUP_LOADING_TYPES {
    SIMPLE_MESSAGE = 'SIMPLE_MESSAGE',
    RECOMMENDING_OVERLAYS = 'RECOMMENDING_OVERLAYS',
}

export type SimpleMessageLoadingContextType = {
    loadingMsg: string;
};
export type SceneGroupLoadingContextType = SimpleMessageLoadingContextType;
export type ImmutableSceneGroupLoadingContextType = ImmutableMap<SimpleMessageLoadingContextType>;

export enum OPTIONS_PAGE_VIDEO_INIT_TYPE {
    VOICEOVER = 'voiceover',
    TALKING_HEAD = 'talking head',
    TEXT_TO_VIDEO = 'text to video',
    TEXT_TO_AI_VOICEOVER = 'text to ai voiceover',
    TEXT_TO_AI_AVATAR = 'text to ai avatar',
}

export const VIDEO_SAMPLE_MODAL_CALLBACK_TYPE = OPTIONS_PAGE_VIDEO_INIT_TYPE;

export enum DASHBOARD_VIDEO_INIT_TYPE {
    CREATE_FROM_SCRATCH = 'create from scratch',
}

export const VIDEO_INIT_TYPE = {
    ...OPTIONS_PAGE_VIDEO_INIT_TYPE,
    ...DASHBOARD_VIDEO_INIT_TYPE,
};

export type VIDEO_INIT_TYPE = OPTIONS_PAGE_VIDEO_INIT_TYPE | DASHBOARD_VIDEO_INIT_TYPE;

export const VIDEO_INIT_TYPE_TO_NARRATIVE_THREAD: Record<
    OPTIONS_PAGE_VIDEO_INIT_TYPE,
    NarrativeThreadEnum | null
> = {
    [VIDEO_INIT_TYPE.VOICEOVER]: NarrativeThreadEnum.VOICEOVER,
    [VIDEO_INIT_TYPE.TALKING_HEAD]: NarrativeThreadEnum.TALKING_HEAD,
    [VIDEO_INIT_TYPE.TEXT_TO_VIDEO]: NarrativeThreadEnum.TEXT,
    [VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER]: NarrativeThreadEnum.VOICEOVER,
    [VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR]: NarrativeThreadEnum.TALKING_HEAD,
};

/**
 * string is in form "color_0" and the number is the colour value
 */
export type VideoColorMap = Record<string, number>[];

export type VideoApprovalStatusData = {
    status: VideoApprovalStatusEnum | null;
    assignee: number | null;
};

export enum VoiceDrivenSceneCreationModalOptions {
    RECORD = 'record',
    UPLOAD = 'upload',
    USE_EXISTING_UPLOAD = 'use existing upload',
    EXIT = 'exit',
}

export type AsyncGenerationSceneGroupData = {
    voiceoverSourceData: AudioSourceData | null;
    mediaSourceData: MediaSourceData | null;
    avatarSourceData: MediaSourceData | null;
    volume: number;
    playbackRegions: [number, number][];
    transcriptWords: TranscriptWord[];
};

export type AsyncGenerationData = {
    sceneGroups: AsyncGenerationSceneGroupData[];
};

export type RecommendedBlueprint = {
    blueprintId: number | null;
    customBlueprintId: number | null;
    recentVideoId: number | null;
};

export const UNKNOWN_SAVE_ERROR_MESSAGE =
    'Something went wrong with that submission. Please try again!';

export const isValidErrorCode = (value: unknown): value is ErrorEnum => {
    return Object.values(ErrorEnum).includes(value as ErrorEnum);
};

export const DEFAULT_TEMPLATE_ID = 'assembly';
